"use client";

import { api } from "~/utils/api";
import { useCallback, useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { cn } from "~/utils/fontsConfig";
import {
    isFollowing as isFollowingCheck,
    unfollowUser,
    unfollowCommunity,
    follow,
} from "./_actions";
import { useQuery } from "@tanstack/react-query";
import { Button, buttonVariants } from "../themed/Button";
import { Input } from "../themed/Input";
import { toastHandler } from "~/components/ToastHandler";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "../themed/Dialog";
import { useFollowingStore } from "~/state/store";
import { VariantProps } from "class-variance-authority";
import { RiArrowRightCircleLine } from "react-icons/ri";

const ConfirmUnfollowModal = ({
    open,
    setOpen,
    onUnfollow,
    type,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    onUnfollow: () => void;
    type: "community" | "user";
}) => {
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>
                        Unfollow {type === "community" ? "Community" : "Person"}
                    </DialogTitle>
                    <DialogDescription>
                        Are you sure you want to unfollow this{" "}
                        {type === "community" ? "community" : "person"}? You'll
                        no longer get any notifications for this{" "}
                        {type === "community" ? "community" : "person"}
                    </DialogDescription>
                </DialogHeader>
                <div className="flex w-full items-center justify-center">
                    <Button
                        onClick={() => {
                            onUnfollow();
                        }}
                        variant="secondary"
                        className="w-1/2 border border-black"
                    >
                        Unfollow
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

type FollowButtonProps = {
    hideIfFollowing?: boolean;
    variant?: VariantProps<typeof buttonVariants>["variant"];
    variantFollowing?: VariantProps<typeof buttonVariants>["variant"];
    className?: string;
    classNameFollowing?: string;
    showSubmitSuffix?: boolean;
    text?: string;
    initialValue?: boolean;
    onFollowingUpdate?: (isFollowing: boolean) => void;
    eventSlug?: string;
} & (
    | {
          communityId: string;
          userId?: never;
      }
    | {
          userId: string;
          communityId?: never;
      }
);

export const FollowButton = ({
    communityId,
    userId,
    text,
    initialValue,
    hideIfFollowing,
    variant,
    variantFollowing,
    className,
    classNameFollowing,
    showSubmitSuffix,
    onFollowingUpdate,
    eventSlug,
}: FollowButtonProps) => {
    const { data: session, status } = useSession();
    const [email, setEmail] = useState<string>("");
    const [isConfirmUnfollowOpen, setIsConfirmUnfollowOpen] = useState(false);
    const { email: storeEmail, setEmail: setStoreEmail } = useFollowingStore();
    const followersCtx = api.useUtils().followers;
    const buttonText = text ?? "Follow";

    const {
        data: isFollowing,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ["isFollowing", communityId, userId, email],
        queryFn: async () =>
            await isFollowingCheck({
                communityId: communityId,
                followedUserId: userId,
                email: email || storeEmail || undefined,
            }),
        initialData: initialValue,
        refetchOnMount: initialValue !== undefined ? false : true,
        enabled:
            !!(communityId || userId) &&
            (!!session?.user.id || !!email || !!storeEmail),
    });

    const [isEmailInputVisible, setIsEmailInputVisible] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const handleUnfollow = communityId
        ? () => unfollowCommunity({ communityId, eventSlug })
        : () => unfollowUser({ userId: userId! });

    useEffect(() => {
        if (isFollowing !== undefined) onFollowingUpdate?.(isFollowing);
    }, [isFollowing]);

    const handleFollowClick = useCallback(async () => {
        if (!session?.user && !email) {
            setIsEmailInputVisible(true);
            return;
        }

        const params = communityId
            ? { communityId, email: email || undefined }
            : { userId, email: email || undefined };

        toastHandler({
            loadingMessage: "Sending follow request",
            successMessage: "Followed!",
            errorMessage: `You're already following this ${communityId ? "community" : "person"}`,
            operation: async () => {
                await follow(params);
            },
            onSuccess: () => {
                setIsEmailInputVisible(false);
                if (email) setStoreEmail(email);
                followersCtx.invalidate();
                setIsConfirmUnfollowOpen(false);
                refetch();
            },
        });
    }, [session, email, communityId, userId]);

    useEffect(() => {
        if (status === "unauthenticated") {
            setIsDisabled(isEmailInputVisible && !email);
            return;
        }
        setIsDisabled(isLoading);
    }, [session, isEmailInputVisible, email, isLoading]);

    if (isFollowing) {
        if (hideIfFollowing) return null;
        return (
            <>
                <Button
                    disabled={isDisabled}
                    onClick={() => setIsConfirmUnfollowOpen(true)}
                    className={cn(className, classNameFollowing)}
                    variant={variantFollowing ?? "tertiary"}
                >
                    Following
                </Button>
                <ConfirmUnfollowModal
                    open={isConfirmUnfollowOpen}
                    setOpen={setIsConfirmUnfollowOpen}
                    type={communityId ? "community" : "user"}
                    onUnfollow={async () => {
                        if (!session?.user) {
                            setStoreEmail(null);
                            setEmail("");
                            followersCtx.invalidate();
                            setIsConfirmUnfollowOpen(false);
                            return;
                        }
                        await handleUnfollow();
                        followersCtx.invalidate();
                        refetch();
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Input
                value={email}
                onChange={(e) => setEmail(e.target.value as string)}
                type="text"
                placeholder="Enter your email address"
                className={cn(!isEmailInputVisible && "hidden")}
                isSuffixInteractive={showSubmitSuffix}
                suffixComponent={
                    showSubmitSuffix && (
                        <Button
                            disabled={isDisabled}
                            onClick={(e) => {
                                e.preventDefault();
                                handleFollowClick();
                            }}
                            variant="secondary"
                            size="md"
                            className="px-2 py-4"
                        >
                            <RiArrowRightCircleLine className="h-6 w-6" />
                        </Button>
                    )
                }
            />
            <Button
                disabled={isDisabled}
                onClick={(e) => {
                    e.preventDefault();
                    handleFollowClick();
                }}
                variant={variant ?? "default"}
                className={cn("grow", className)}
            >
                {buttonText}
            </Button>
        </>
    );
};
